/**
 * Lightweight breadcrumb trail.
 *
 * Keeps the last N user actions in memory so that when an error is reported we
 * know how the user got there — no need to ask for replication steps.
 * Nothing is persisted and no free-text user content is recorded (GDPR).
 */

export type BreadcrumbCategory = 'navigation' | 'ui' | 'network' | 'auth' | 'info';

export interface Breadcrumb {
  t: string; // ISO timestamp
  category: BreadcrumbCategory;
  message: string;
  data?: Record<string, string | number | boolean | null>;
}

const MAX_BREADCRUMBS = 20;
const MAX_MESSAGE_LENGTH = 160;

const buffer: Breadcrumb[] = [];

export function addBreadcrumb(
  category: BreadcrumbCategory,
  message: string,
  data?: Record<string, string | number | boolean | null>,
): void {
  try {
    buffer.push({
      t: new Date().toISOString(),
      category,
      message: String(message).slice(0, MAX_MESSAGE_LENGTH),
      data,
    });
    if (buffer.length > MAX_BREADCRUMBS) buffer.splice(0, buffer.length - MAX_BREADCRUMBS);
  } catch {
    // breadcrumbs must never break the app
  }
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer];
}

export function clearBreadcrumbs(): void {
  buffer.length = 0;
}

/**
 * Global click listener that records interactions with buttons/links by their
 * accessible label only (never input values).
 */
export function installInteractionBreadcrumbs(): void {
  if (typeof document === 'undefined') return;

  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target as HTMLElement | null;
        const actionable = target?.closest?.('button, a, [role="button"], [role="tab"]') as
          | HTMLElement
          | null;
        if (!actionable) return;

        const label =
          actionable.getAttribute('aria-label') ||
          actionable.getAttribute('data-tour') ||
          actionable.textContent?.trim().slice(0, 60) ||
          actionable.tagName.toLowerCase();

        addBreadcrumb('ui', `click: ${label}`, {
          tag: actionable.tagName.toLowerCase(),
        });
      } catch {
        // ignore
      }
    },
    { capture: true, passive: true },
  );
}
